import React, {useEffect, useState} from 'react';
import '../../../app.scss';
import './sidebar-wrapper.scss';
import {Button} from 'edozvola-frontend-lib/dist/ui/components/button';
import {faBars, faX} from '@fortawesome/free-solid-svg-icons';
import {declassify} from 'edozvola-frontend-lib/dist/util';
import {useNavigate} from 'react-router-dom';
import {useAppSelector} from 'app/config/store';
import {v4 as uuid} from 'uuid';
import {useDispatch} from 'react-redux';
import {push as NavigationSidebar} from 'react-burger-menu';
import {openConfirmationModalWithCallbacks, setFormChanged} from 'app/shared/reducers/case-form-change';
import {SidebarItemPair} from 'app/shared/layout/sidebar/configuration/sidebar-configuration';
import {Col, Collapse, Row} from 'reactstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import { getInvalidCaseActs, getInvalidTabs } from 'app/shared/util/validation-errors.util';
import {AUTHORITIES, PUBLIC_WEB_INPUT_STATUS} from 'app/config/constants';
import {getCaseContext} from 'app/shared/util/context-util';
import {isEqual} from "lodash";
import {ConfirmationModal} from 'app/shared/modal/confirmation-modal';

const SidebarWrapper = ({
                          children,
                          navigationItems = [] as SidebarItemPair[],
                          relativePath = false,
                          returnUrl = 'case-submission',
                          webId,
                        }) => {
  const [navbarOpened, setNavbarOpened] = useState(true);
  const [items, setItems] = useState(navigationItems);
  const navigate = useNavigate();
  const showHideButton = false;
  const caseFormChange = useAppSelector(state => state.caseFormChange);
  const caseEntity = useAppSelector(state => state.caseEntity);
  const dispatch = useDispatch();
  const [selectedItem, setSelectedItem] = useState(null);
  const [unsubmittedWarningModalShown, setUnsubmittedWarningModalShown] = useState(false);
  const [hadUnsavedChanges, setHadUnsavedChanges] = useState(false);

  const userAuthorities = useAppSelector(state => state.authentication.account.authorities);
  const hasAnonymousRole = userAuthorities && userAuthorities.includes(AUTHORITIES.NEPRIJAVLJENI);
  const invalidTabs = getInvalidTabs();
  const invalidCaseActs = getInvalidCaseActs();

  const {sidebarItemPair, setSidebarItemPair} = getCaseContext();

  useEffect(() => {
    onCollapseChange(selectedItem, true);
  }, [selectedItem]);

  // when a user directly wants to open a case tab that is not currently visible (via changing a path in browser), sidebarItemPair is null, and we redirect it to case starting page
  (sidebarItemPair === null || sidebarItemPair.sidebarItemPath === undefined) &&
    navigate(
      location.pathname.includes('/case-submission')
        ? `/case-submission/case/${caseEntity.idCase}`
        : location.pathname.includes('/cases-preview')
          ? `/cases-preview/${caseEntity.idCase}`
          : `/cases/${caseEntity.idCase}`,
      {
        state: { from: returnUrl },
      },
    );

  const determineIfLocationIsEqualToNavigationItem = (navigationItemValue: string, item: SidebarItemPair) => {
    const sidebarPath = location.pathname.substring(location.pathname.lastIndexOf('/') + 1);

    const isLocationEqualToNavigationItem = sidebarPath === navigationItemValue;
    if (isLocationEqualToNavigationItem && item && !isEqual(selectedItem, item)) {
      if (item.children.length > 0) {
        const child = item.children.filter(i => sidebarPath === i.sidebarItemPath)[0];

        setSidebarItemPair(child);
      } else {
        setSidebarItemPair(item);
      }
      setSelectedItem(item);
    }

    return isLocationEqualToNavigationItem;
  };

  function onNavigationChangeConfirmed(navbarItemPath: string) {
    if (relativePath) {
      navigate('../' + navbarItemPath, {state: {from: returnUrl}});
    } else {
      navigate(navbarItemPath, {state: {from: returnUrl}});
    }
  }

  const onNavigationItemClicked = (item: SidebarItemPair, collapseAll = false) => {
    if (caseFormChange.formChanged) {
      dispatch(
        openConfirmationModalWithCallbacks({
          onConfirmCallbackFunction: () => {
            setSidebarItemPair(item);
            onNavigationChangeConfirmed(item.sidebarItemPath);
            if (collapseAll) {
              const newState = [...items];
              items.map(item => (item.collapsed = true));

              setItems(newState);
            }
          },
        }),
      );
    } else {
      setSidebarItemPair(item);
      onNavigationChangeConfirmed(item.sidebarItemPath);

      if (collapseAll) {
        const newState = [...items];

        items.map(item => (item.collapsed = true));

        setItems(newState);
      }
    }
  };

  const onCollapseChange = (selectedItem: any, keepOpen: boolean = false) => {
    const newState = [...items];
    items
      .filter(item => !isEqual(item, selectedItem))
      .map(item => {
        item.collapsed = true;
      });
    items
      .filter(item => isEqual(item, selectedItem))
      .map(item => {
        if (keepOpen) {
          item.collapsed = false;
        } else {
          item.collapsed = !item.collapsed;
        }
      });

    setItems(newState);
  };

  const doNavigateBack = () =>
    navigate(returnUrl && returnUrl !== 'undefined' && returnUrl !== 'null' ? `/${returnUrl}` : `/`);

  const navigateOrShowUnsubmittedWarning = () => {
    if (caseEntity.caseStatus === PUBLIC_WEB_INPUT_STATUS) {
      setUnsubmittedWarningModalShown(true);
    } else {
      doNavigateBack();
    }
  };

  const returnToCasesTableActionClicked = () => {
    if (caseFormChange.formChanged) {
      setHadUnsavedChanges(true);
      dispatch(
        openConfirmationModalWithCallbacks({
          onConfirmCallbackFunction: () => navigateOrShowUnsubmittedWarning(),
        }),
      );
    } else {
      setHadUnsavedChanges(false);
      navigateOrShowUnsubmittedWarning();
    }
  };

  return (
    <div style={{height: '100%'}} className="page-content" id={'outer-container'}>
      <NavigationSidebar
        noOverlay
        isOpen={navbarOpened}
        pageWrapId={'page-wrap'}
        outerContainerId={'outer-container'}
        customCrossIcon={<Button size="sm" appendIcon={faX} className={!showHideButton ? 'hidden-button' : ''} />}
        customBurgerIcon={<Button size="sm" prependIcon={faBars} />}
        onClose={() => (showHideButton ? setNavbarOpened(false) : null)}
        onOpen={() => setNavbarOpened(true)}
        id={'navigation-sidebar'}
      >
        {items &&
          items.filter(item => item.visible).map(
            (item, index, filteredItems) =>
                <React.Fragment key={uuid()}>
                  {item.children && item.children.length > 0 && (
                    <a
                      key={uuid()}
                      className={declassify({
                        'error-tab': item.children.some(i => invalidTabs.includes(i.sidebarItemKey)),
                        'bm-item': true,
                        'pb-4': index === filteredItems.length - 1
                      })}
                      onClick={() => onCollapseChange(item)}
                    >
                      {item.sidebarItemValue}
                      <FontAwesomeIcon icon={!item.collapsed ? 'chevron-up' : 'chevron-down'} className={'ms-2 font-size-small'} />
                    </a>
                  )}
                  {(!item.children || item.children.length === 0) && item.sidebarItemPath && (
                    <a
                      key={uuid()}
                      onClick={() => onNavigationItemClicked(item, true)}
                      className={declassify({
                        'error-tab': invalidCaseActs.length > 0 && (item.sidebarItemPath === 'amendment-acts' || item.sidebarItemPath === 'acts'),
                        'navigation-item-active': determineIfLocationIsEqualToNavigationItem(item.sidebarItemPath, item),
                        'bm-item': true,
                        'pb-4': index === filteredItems.length - 1
                      })}
                    >
                      {item.sidebarItemValue}
                    </a>
                  )}
                  <Collapse isOpen={!item.collapsed}>
                    {item.children &&
                      item.children.map(
                        childItem =>
                          childItem.visible && (
                            <React.Fragment key={uuid()}>
                              <a
                                key={uuid()}
                                onClick={() => onNavigationItemClicked(childItem)}
                                className={declassify({
                                  'navigation-item-active': determineIfLocationIsEqualToNavigationItem(childItem.sidebarItemPath, item),
                                  'bm-item': true,
                                  'bm-subitem': true,
                                  'error-tab': invalidTabs.includes(childItem.sidebarItemKey),
                                  'pb-4': index === filteredItems.length - 1
                                })}
                              >
                                {childItem.sidebarItemValue}
                              </a>
                            </React.Fragment>
                          ),
                      )}
                  </Collapse>
                </React.Fragment>
          )}
      </NavigationSidebar>
      <div
        id={'page-wrap'}
        className={declassify({
          'navbar-content navbar-opened': navbarOpened,
          'navbar-content navbar-closed': !navbarOpened,
        })}
      >
        <Row>
          <Col xs={12}>
            <div className={'mb-3 mt-3 d-flex gap-3 align-items-center'}>
              <div className={'d-flex gap-3 align-items-center hand'}>
                <FontAwesomeIcon icon={'arrow-left'} onClick={returnToCasesTableActionClicked} />
                <div className={'fw-bold'} onClick={returnToCasesTableActionClicked}>
                  Povratak
                </div>
                <div className="p-3 text-danger">
                  {caseEntity.caseStatus === PUBLIC_WEB_INPUT_STATUS && (
                    <>
                      {hasAnonymousRole ? (
                        <div>
                          Rad na ovom zahtjevu možete nastaviti unutar 30 dana od kreiranja zahtjeva pomoću šifre <strong>{webId}</strong> i
                          OIB-a ili MB-a podnositelja
                        </div>
                      ) : (
                        <div>
                          Rad na ovom zahtjevu možete nastaviti unutar 30 dana od kreiranja zahtjeva pomoću šifre <strong>{webId}</strong>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        {children}
      </div>
      <ConfirmationModal
        header="Zahtjev nije predan"
        modalOpened={unsubmittedWarningModalShown}
        bodyMessage={`Ovaj zahtjev nije predan nadležnom uredu.<br><br>Rad na ovom zahtjevu možete nastaviti unutar 30 dana od kreiranja zahtjeva unosom šifre <strong>${webId}</strong> i OIB-a ili MB-a podnositelja na izborniku Predaja zahtjeva.`}
        confirmButtonLabel="Izađi iz zahtjeva"
        cancelButtonLabel="Odustani"
        onConfirm={doNavigateBack}
        onCancel={() => {
          setUnsubmittedWarningModalShown(false);
          if (hadUnsavedChanges) {
            dispatch(setFormChanged(true));
          }
        }}
      />
    </div>
  );
};

export default SidebarWrapper;
